import React, { useState, useEffect, useMemo } from 'react';
import { getDoc, getDocs, collection, query, where, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDate } from '../utils/dateFormat';
import { toUtcMs } from '../utils/dateTimeUtils';
import { migrateScheduleConfig } from '../utils/scheduleStrategy';
import { getTournamentLastMatchSlot, isSlotTaken } from '../utils/scheduleStrategy';
import { generateSlotKeys, getAvailabilityForPlayers, isSlotAvailableForPlayer } from '../services/playerAvailabilityService';
import './RescheduleRequestModal.css';

function toDateInputValue(d) {
  if (!d) return '';
  const ms = toUtcMs(d);
  if (ms == null) return '';
  const date = new Date(ms);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Get participant IDs for the match (all players in the match). */
function getParticipantIds(m) {
  if (!m) return [];
  if (m.matchType === 'doubles') {
    return [...(m.team1Players || []), ...(m.team2Players || [])];
  }
  return [m.player1Id, m.player2Id].filter(Boolean);
}

/** Get weekday 1–7 (1=Mon, 7=Sun) from YYYY-MM-DD string. */
function getWeekdayFromDateStr(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

export default function RescheduleRequestModal({ match, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [lastSlot, setLastSlot] = useState(null);
  const [otherMatches, setOtherMatches] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [availabilityByPlayer, setAvailabilityByPlayer] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('18:00');

  const matchId = match?.id;
  const tournamentId = match?.tournamentId;
  const participantIds = useMemo(() => getParticipantIds(match), [match]);

  useEffect(() => {
    if (!tournamentId || !matchId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [tSnap, mSnap] = await Promise.all([
          getDoc(doc(db, 'tournaments', tournamentId)),
          getDocs(query(collection(db, 'matches'), where('tournamentId', '==', tournamentId)))
        ]);
        if (cancelled) return;

        const tData = tSnap.exists() ? tSnap.data() : {};
        const config = migrateScheduleConfig(tData.scheduleConfig || {});
        setScheduleConfig(config);
        setTournament({ id: tSnap.id, ...tData });

        const allMatches = mSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const last = getTournamentLastMatchSlot(allMatches);
        setLastSlot(last);
        setOtherMatches(allMatches);

        const ids = getParticipantIds(match);
        if (tData.askPlayerAvailability && ids.length > 0) {
          const avail = await getAvailabilityForPlayers(tournamentId, ids);
          if (!cancelled) setAvailabilityByPlayer(avail);
        } else {
          if (!cancelled) setAvailabilityByPlayer(null);
        }

        const today = new Date();
        const todayStr = toDateInputValue(today);
        setSelectedDate(todayStr);
        setSelectedTime(config.scheduleStart || '18:00');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tournamentId, matchId, match]);

  const today = new Date();
  const todayStr = toDateInputValue(today);
  const fallbackMax = new Date(today);
  fallbackMax.setMonth(fallbackMax.getMonth() + 6);
  const maxDateStr = lastSlot ? toDateInputValue(lastSlot.date) : toDateInputValue(fallbackMax);

  const useAvailabilityFilter = tournament?.askPlayerAvailability && availabilityByPlayer && participantIds.length > 0;

  const availableTimesForSelectedDate = useMemo(() => {
    if (!selectedDate || !scheduleConfig || !otherMatches.length) return null;
    const weekday = getWeekdayFromDateStr(selectedDate);
    const weekdays = scheduleConfig.weekdays || [1, 3, 5];
    if (weekday == null || !weekdays.includes(weekday)) return [];

    const allSlotKeys = generateSlotKeys(scheduleConfig);
    const dateKey = selectedDate;
    const [y, m, d] = selectedDate.split('-').map(Number);
    const slotDate = new Date(y, m - 1, d);

    const times = [];
    for (const key of allSlotKeys) {
      if (!key.startsWith(`${weekday}_`)) continue;
      const time = key.slice(String(weekday).length + 1);
      if (!useAvailabilityFilter) {
        if (!isSlotTaken(slotDate, time, otherMatches, matchId)) times.push(time);
        continue;
      }
      let allAvailable = true;
      for (const pid of participantIds) {
        const avail = availabilityByPlayer.get(pid);
        if (!isSlotAvailableForPlayer(key, avail, dateKey)) {
          allAvailable = false;
          break;
        }
      }
      if (allAvailable && !isSlotTaken(slotDate, time, otherMatches, matchId)) times.push(time);
    }
    return times.sort();
  }, [selectedDate, scheduleConfig, otherMatches, matchId, useAvailabilityFilter, availabilityByPlayer, participantIds]);

  useEffect(() => {
    if (useAvailabilityFilter && Array.isArray(availableTimesForSelectedDate) && availableTimesForSelectedDate.length > 0 && !availableTimesForSelectedDate.includes(selectedTime)) {
      setSelectedTime(availableTimesForSelectedDate[0]);
    }
  }, [useAvailabilityFilter, availableTimesForSelectedDate, selectedTime]);

  const canSubmitTime = !useAvailabilityFilter || !Array.isArray(availableTimesForSelectedDate) || availableTimesForSelectedDate.length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const dateVal = selectedDate?.trim();
    const timeVal = selectedTime?.trim() || '18:00';
    if (!dateVal) {
      setError('Please select a date.');
      return;
    }

    const [y, m, d] = dateVal.split('-').map(Number);
    const slotDate = new Date(y, m - 1, d);

    if (slotDate < new Date(new Date().setHours(0, 0, 0, 0))) {
      setError('Selected date cannot be in the past.');
      return;
    }

    if (lastSlot) {
      const lastMs = toUtcMs(lastSlot.date);
      const lastDate = lastMs != null ? new Date(lastMs) : null;
      const lastDay = lastDate ? new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate()).getTime() : 0;
      const slotDay = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate()).getTime();
      if (lastDate && slotDay > lastDay) {
        setError(`Date must be on or before the last match (${formatDate(lastSlot.date)}).`);
        return;
      }
      if (slotDay === lastDay) {
        const slotMins = parseInt(timeVal.split(':')[0], 10) * 60 + parseInt(timeVal.split(':')[1] || 0, 10);
        const lastParts = (lastSlot.time || '23:59').split(':');
        const lastMins = parseInt(lastParts[0], 10) * 60 + parseInt(lastParts[1] || 0, 10);
        if (slotMins > lastMins) {
          setError(`On the last match day, time must be at or before ${lastSlot.time}.`);
          return;
        }
      }
    }

    if (isSlotTaken(slotDate, timeVal, otherMatches, matchId)) {
      setError('This slot is already taken by another match.');
      return;
    }

    if (useAvailabilityFilter && Array.isArray(availableTimesForSelectedDate) && availableTimesForSelectedDate.length > 0 && !availableTimesForSelectedDate.includes(timeVal)) {
      setError('This time is not available for one or more players in this match.');
      return;
    }

    onConfirm(slotDate, timeVal);
  };

  if (loading) {
    return (
      <div className="reschedule-modal-overlay" onClick={onCancel}>
        <div className="reschedule-modal" onClick={(e) => e.stopPropagation()}>
          <div className="reschedule-modal-header">
            <h2>Request reschedule</h2>
            <button type="button" className="reschedule-modal-close" onClick={onCancel} aria-label="Close">×</button>
          </div>
          <p className="reschedule-modal-loading">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reschedule-modal-overlay" onClick={onCancel}>
      <div className="reschedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="reschedule-modal-header">
          <h2>Request reschedule</h2>
          <button type="button" className="reschedule-modal-close" onClick={onCancel} aria-label="Close">×</button>
        </div>
        <p className="reschedule-modal-desc">
          Choose your preferred date and time. You can pick any day or time until the last match in the tournament.
        </p>
        {lastSlot && (
          <p className="reschedule-modal-last">Last match: {formatDate(lastSlot.date)} at {lastSlot.time}</p>
        )}
        <form onSubmit={handleSubmit} className="reschedule-modal-form">
          <div className="reschedule-form-row">
            <label>
              <span>Date</span>
              <input
                type="date"
                value={selectedDate}
                min={todayStr}
                max={maxDateStr || undefined}
                onChange={(e) => setSelectedDate(e.target.value)}
                required
              />
            </label>
          </div>
          <div className="reschedule-form-row">
            <label>
              <span>Time</span>
              {useAvailabilityFilter && Array.isArray(availableTimesForSelectedDate) ? (
                <>
                  <select
                    value={availableTimesForSelectedDate.includes(selectedTime) ? selectedTime : (availableTimesForSelectedDate[0] ?? '')}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="reschedule-time-select"
                  >
                    {availableTimesForSelectedDate.length === 0 ? (
                      <option value="">No slots when all players are available</option>
                    ) : (
                      availableTimesForSelectedDate.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))
                    )}
                  </select>
                  {availableTimesForSelectedDate.length === 0 && (
                    <p className="reschedule-modal-hint" style={{ marginTop: '6px' }}>
                      Pick another day or ask other players to add availability.
                    </p>
                  )}
                </>
              ) : (
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  min={selectedDate === maxDateStr && lastSlot ? '00:00' : undefined}
                  max={selectedDate === maxDateStr && lastSlot ? lastSlot.time : undefined}
                />
              )}
            </label>
          </div>
          {scheduleConfig && !useAvailabilityFilter && (
            <p className="reschedule-modal-hint">
              Tournament schedule: {scheduleConfig.scheduleStart}–{scheduleConfig.scheduleEnd}
            </p>
          )}
          {scheduleConfig && useAvailabilityFilter && (
            <p className="reschedule-modal-hint">
              Only times when all players in this match are available are shown.
            </p>
          )}
          {error && <p className="reschedule-modal-error">{error}</p>}
          <div className="reschedule-modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={!canSubmitTime}>
              Request reschedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
